import type { BaseEntity, UUID } from './common';
import type { PublicProductCardWithUrl, SalePriceCents, VariantAvailabilityState } from './product';

export const OFFER_CAMPAIGN_MAX_ITEMS = 48;

export type OfferCampaignStatus = 'scheduled' | 'live' | 'ended' | 'inactive';
export type PublicOfferCampaignState = 'live' | 'ended' | 'unavailable';

export interface OfferCampaign extends BaseEntity {
  readonly store_id: UUID;
  readonly public_slug: string;
  readonly title: string;
  readonly short_description: string | null;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly is_active: boolean;
}

export interface OfferCampaignItem extends BaseEntity {
  readonly campaign_id: UUID;
  readonly store_id: UUID;
  readonly product_id: UUID;
  readonly variant_id: UUID;
}

export interface OfferCampaignItemWithSellable extends OfferCampaignItem {
  readonly product_name: string;
  readonly category_name: string | null;
  readonly variant_option_summary: string;
  readonly variant_price_cents: number;
  readonly variant_sale_price_cents: SalePriceCents;
  readonly currency: 'ILS';
  readonly available: number;
  readonly active: boolean;
  readonly product_published: boolean;
  readonly is_default_variant: boolean;
}

export interface OfferCampaignWithItems extends OfferCampaign {
  readonly status: OfferCampaignStatus;
  readonly items: readonly OfferCampaignItemWithSellable[];
}

export interface OfferCampaignFormInput {
  readonly store_id: UUID;
  readonly title: string;
  readonly short_description: string | null;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly is_active: boolean;
  readonly variant_ids: readonly UUID[];
}

export type CreateOfferCampaignInput = OfferCampaignFormInput;

export interface UpdateOfferCampaignInput extends OfferCampaignFormInput {
  readonly campaign_id: UUID;
}

export interface DeleteOfferCampaignInput {
  readonly campaign_id: UUID;
}

export interface OfferCampaignSellableOption {
  readonly product_id: UUID;
  readonly variant_id: UUID;
  readonly product_name: string;
  readonly category_id: UUID | null;
  readonly category_name: string | null;
  readonly option_summary: string;
  readonly is_default_variant: boolean;
  readonly price_cents: number;
  readonly sale_price_cents: number;
  readonly currency: 'ILS';
  readonly available: number;
  readonly active: boolean;
  readonly product_published: boolean;
  readonly availability_state: VariantAvailabilityState;
  readonly isSelectable: boolean;
}

export interface PublicOfferCampaignProduct extends PublicProductCardWithUrl {
  readonly campaign_variant_id: UUID;
  readonly campaign_variant_option_summary: string;
  readonly campaign_variant_is_default: boolean;
  readonly campaign_regular_price_cents: number;
  readonly campaign_sale_price_cents: number;
  readonly campaign_effective_price_cents: number;
}

export interface PublicLiveOfferCampaignSummary {
  readonly id: UUID;
  readonly store_slug: string;
  readonly public_slug: string;
  readonly title: string;
  readonly short_description: string | null;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly products: readonly PublicOfferCampaignProduct[];
}

export type PublicOfferCampaignPage =
  | ({ readonly state: 'live' } & PublicLiveOfferCampaignSummary)
  | { readonly state: 'ended' }
  | { readonly state: 'unavailable' };

export type OfferCampaignErrorKey =
  | 'admin.offerCampaigns.errors.invalidWindow'
  | 'admin.offerCampaigns.errors.emptySelection'
  | 'admin.offerCampaigns.errors.ineligibleSellable'
  | 'admin.offerCampaigns.errors.titleRequired'
  | 'admin.offerCampaigns.errors.titleTooLong'
  | 'admin.offerCampaigns.errors.descriptionTooLong'
  | 'admin.offerCampaigns.errors.titleInvalid'
  | 'admin.offerCampaigns.errors.itemLimitExceeded'
  | 'admin.offerCampaigns.errors.duplicateSelection'
  | 'admin.entitlements.promotionKitBlocked'
  | 'admin.offerCampaigns.errors.permissionDenied'
  | 'admin.offerCampaigns.errors.notFound'
  | 'common.errors.networkFailed'
  | 'common.errors.unknown';
